/**
 * CallWorkspace — the full-screen "Call Mode" workspace. Auto-opens (via
 * CallWorkspaceContext) when a call connects to a known contact, giving the
 * recruiter everything at their fingertips: the study script, an all-visible
 * prescreen (fill out of order while talking), the protocol/ICF AI wizard, the
 * documents, a notes scratchpad, and inline scheduling/disposition.
 *
 * Layering: z-40 — deliberately BELOW the call dock (z-50) so mute/hang-up
 * stay clickable. Mounted ONCE in CRMApp, outside <Routes>, so it survives
 * navigation. Minimizes to a pill; the call continues.
 *
 * Desktop: two columns — LEFT the Script + Notes (always visible: read from the
 * script, jot notes), RIGHT a tabbed reference the recruiter clicks through
 * (Prescreen / AI wizard / Docs / Next steps). Mobile: one column with a full
 * tab switcher across every pane.
 *
 * Outreach lists (docs/crm-outreach-lists-plan.md §B6): when the person on the
 * call is an open member of an outreach list assigned to the viewer, the
 * list's outcome control sits in the header (top of Next steps on a phone) —
 * the recruiter knows how the call went at exactly this moment. Logging closes
 * the workspace; while a list is being worked, "Log & next" also moves the
 * Next-up bar to the next person. The close never throws the call's notes
 * away: an unsaved Notes draft is saved first, and if that save fails the
 * workspace stays open with the draft still in the pad.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Maximize2, Minus, Pencil, Phone, PhoneCall, PhoneOff, X } from 'lucide-react';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useWorkQueue } from '../../WorkQueueContext';
import OutcomeControl from '../../outreach/OutcomeControl';
import { getLeadOutreach } from '../../outreach/api';
import type { LeadOutreachInfo } from '../../outreach/types';
import { Contact, ContactDetailResponse, StudyDetailResponse, StudyDocument, StudyOption, studyInternalLabel } from '../../types';
import { ageFromDob, formatDob, formatPhone, getPref, setPref, useTeam } from '../../lib';
import { useNow } from '../../useNow';
import { useSoftphone } from '../../contexts/SoftphoneContext';
import { useCallWorkspace } from '../../contexts/CallWorkspaceContext';
import LifecycleSelect from '../LifecycleSelect';
import EditContactModal from '../EditContactModal';
import PrescreenAllPane, { PrescreenPaneApi } from '../prescreen/PrescreenAllPane';
import PrescreenRunner from '../prescreen/PrescreenRunner';
import CommsThread from '../CommsThread';
import Composer from '../Composer';
import CallScriptPane from './CallScriptPane';
import AiWizardPanel from './AiWizardPanel';
import StudyDocsPanel from './StudyDocsPanel';
import { openStudyDoc } from '../forms/openStudyDoc';
import NotesScratchpad, { type NotesPadApi } from './NotesScratchpad';
import CallDispositionBar from './CallDispositionBar';
import TranscriptSlot from './TranscriptSlot';
import { Z } from '../ui';

// The WCR enrollment line the patient calls/texts — kept visible so a recruiter
// can read it back to the lead without leaving the call workspace.
const WCR_ENROLLMENT_PHONE = '252-659-5222';

// Desktop only: the left column's two stacked windows (the active Script/
// Summary/Communicator pane on top, Notes below) are split by a drag handle.
// Notes defaults to ~3 lines tall and the recruiter drags the handle up/down to
// resize it; the chosen height persists per browser.
const NOTES_H_PREF = 'callworkspace.notesHeight';
const DEFAULT_NOTES_H = 150; // ≈ "Notes" label + a 3-line textarea + the Save row
const MIN_NOTES_H = 72;      // never collapse the pad below ~1 line
const NOTES_RESIZE_STEP = 24; // keyboard nudge (Shift = 2×)

// Desktop: the LEFT column is a tabbed pane — Script / Summary / Communicator
// (the recruiter toggles to the lead's unified comms feed mid-call) — with the
// Notes scratchpad pinned always-visible beneath it; the recruiter clicks
// through Prescreen / AI wizard / Docs / Next steps on the RIGHT. On mobile
// every pane is a single top tab.
type LeftTab = 'script' | 'summary' | 'comms';
type RightTab = 'prescreen' | 'wizard' | 'docs' | 'next';
type Tab = LeftTab | 'notes' | RightTab;
// Panes that live in the LEFT section — drives the mobile show/hide and the
// right-pane fallback (a left pane on mobile defaults the right column to
// Prescreen so it's never blank).
const LEFT_TABS = new Set<Tab>(['script', 'summary', 'comms', 'notes']);

const LEFT_PANE_TABS: { key: LeftTab; label: string }[] = [
  { key: 'script', label: 'Script' },
  { key: 'summary', label: 'Summary' },
  { key: 'comms', label: 'Communicator' },
];
const RIGHT_TABS: { key: RightTab; label: string }[] = [
  { key: 'prescreen', label: 'Prescreen' },
  { key: 'wizard', label: 'AI wizard' },
  { key: 'docs', label: 'Docs' },
  { key: 'next', label: 'Next steps' },
];
const MOBILE_TABS: { key: Tab; label: string }[] = [
  ...LEFT_PANE_TABS,
  { key: 'notes', label: 'Notes' },
  ...RIGHT_TABS,
];

/** mm:ss for a number of seconds. */
function mmss(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** Live mm:ss since the call connected. */
function useElapsed(startedAt: number | null): string {
  // Same 1s cadence, off the ONE shared clock (D-9) — and, as before, no timer
  // at all until a call is actually connected.
  const now = useNow(1000, Boolean(startedAt));
  if (!startedAt) return '00:00';
  return mmss((now - startedAt) / 1000);
}

export default function CallWorkspace() {
  const { open, minimized, activeContactId, minimize, restore, close } = useCallWorkspace();
  const { active } = useSoftphone();

  if (!open || !activeContactId) return null;
  // Keep the panel MOUNTED while minimized (just hidden) so in-progress AI chat
  // and an unsaved notes draft survive minimize → restore. display:none on the
  // wrapper also hides the panel's fixed-position child.
  return (
    <>
      {minimized && (
        <button
          type="button"
          onClick={restore}
          className={`fixed bottom-[calc(13.5rem+env(safe-area-inset-bottom))] right-4 ${Z.overlay} inline-flex items-center gap-2 rounded-full bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-lg transition-colors duration-150 hover:bg-primary-700 lg:bottom-40`}
        >
          <PhoneCall className="h-4 w-4" /> Call workspace{active?.name ? ` · ${active.name}` : ''}
          <Maximize2 className="h-4 w-4" />
        </button>
      )}
      <div className={minimized ? 'hidden' : 'contents'}>
        <CallWorkspacePanel contactId={activeContactId} onMinimize={minimize} onClose={close} />
      </div>
    </>
  );
}

function CallWorkspacePanel({ contactId, onMinimize, onClose }: { contactId: string; onMinimize: () => void; onClose: () => void }) {
  const { active, startedAt, lastCall, callStatus } = useSoftphone();
  const { byId: usersById } = useTeam(); // sender attribution in the Communicator feed
  const elapsed = useElapsed(startedAt);
  // The call has ended but the recruiter is still here (finishing notes /
  // disposition). Show a neutral "Call ended" pill with the final duration
  // instead of the live green timer, which would otherwise reset to 00:00.
  const callEnded = callStatus === 'idle';
  const endedDuration = lastCall?.durationMs ? mmss(lastCall.durationMs / 1000) : null;

  const [detail, setDetail] = useState<ContactDetailResponse | null>(null);
  const [studies, setStudies] = useState<StudyOption[]>([]);
  const [selectedStudyId, setSelectedStudyId] = useState<string>('');
  const [studyDetail, setStudyDetail] = useState<StudyDetailResponse | null>(null);
  const [studyDetailLoading, setStudyDetailLoading] = useState(false);
  const [tab, setTab] = useState<Tab>('prescreen'); // mobile pane + the desktop RIGHT column; the desktop LEFT column uses leftTab
  const [leftTab, setLeftTab] = useState<LeftTab>('script'); // desktop LEFT column selection (Script / Summary / Communicator)
  const [useRunner, setUseRunner] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [commsToken, setCommsToken] = useState(0); // bumps the Communicator feed to the just-sent message (kept apart from reloadToken so unrelated bumps don't force a scroll-to-bottom)
  const [openingDocId, setOpeningDocId] = useState<string | null>(null);
  // Edit-details modal (name / phone / email / DOB) — opened from the header
  // so the recruiter can capture what the lead says without leaving the call.
  const [editOpen, setEditOpen] = useState(false);
  useEffect(() => { setEditOpen(false); }, [contactId]);
  // One-shot draft pushed from a "Next steps" quick action (booking link, form
  // link) into the Communicator's composer; cleared once the Composer takes it.
  const [composerDraft, setComposerDraft] = useState<{ text: string; mode?: 'sms' } | null>(null);

  const bump = useCallback(() => setReloadToken((t) => t + 1), []);

  // ── Outreach list membership (the outcome control) ─────────────────────
  // Shown only for an open member ASSIGNED TO THE VIEWER: this is their call
  // to log. Everyone else still has the lead page's chip. A failed or not-yet-
  // deployed lookup just leaves the control out — Call Mode must never break
  // over it.
  const { user } = useAuth();
  const meId = user?.id ?? null;
  const queue = useWorkQueue();
  const [outreach, setOutreach] = useState<LeadOutreachInfo['member']>(null);
  // "Log & next" is on by default while a list is being worked: the list is
  // the reason the recruiter is on this call.
  const [logAndNext, setLogAndNext] = useState(true);
  // A new person on the call drops the last one's control at once; a reload
  // for the SAME person (a bump after a stage change) re-reads it in place,
  // so the control does not blink out and back.
  useEffect(() => { setOutreach(null); }, [contactId]);
  useEffect(() => {
    let alive = true;
    getLeadOutreach(contactId)
      .then((info) => { if (alive) setOutreach(info.member); })
      .catch(() => { /* not on a list, or not available yet — no control */ });
    return () => { alive = false; };
  }, [contactId, reloadToken]);
  const myListMember = outreach && outreach.can_log && outreach.assignee?.id === meId ? outreach : null;

  // Logging an outcome closes the workspace — which would unmount the Notes
  // pad and drop whatever the recruiter typed during the call without a word
  // (the pad keeps its draft only in its own state; this panel is kept
  // mounted through minimize for exactly that draft's sake). So the draft is
  // saved first, through the pad's own save. If that save fails the
  // workspace stays open: the outcome is logged, the draft is still in the
  // pad with its error, and a line says so.
  const notesApiRef = useRef<NotesPadApi | null>(null);
  const registerNotesApi = useCallback((apiObj: NotesPadApi | null) => {
    notesApiRef.current = apiObj;
  }, []);
  const [notesLeftUnsaved, setNotesLeftUnsaved] = useState(false);
  useEffect(() => { setNotesLeftUnsaved(false); }, [contactId]);
  const onNotesSaved = useCallback(() => {
    setNotesLeftUnsaved(false);
    bump();
  }, [bump]);
  const closeAfterOutcome = useCallback(async () => {
    const notesSafe = notesApiRef.current ? await notesApiRef.current.flush() : true;
    if (!notesSafe) {
      setNotesLeftUnsaved(true);
      return;
    }
    onClose();
  }, [onClose]);

  // Re-pull the contact (opt-out / lifecycle / worklist state) — wired to the
  // Communicator feed's Realtime ping and after a mid-call send.
  const refreshDetail = useCallback(() => {
    api.get(`/api/crm/contacts/${contactId}`)
      .then((res) => setDetail(res.data))
      .catch(() => { /* a later load retries */ });
  }, [contactId]);

  // A text/email sent from the Communicator while on the call: follow the feed
  // to the new message, refresh the "Next steps" disposition/cadence (a send is
  // an outreach attempt), and re-pull the contact.
  const onCommsSent = useCallback(() => {
    setCommsToken((t) => t + 1);
    bump();
    refreshDetail();
  }, [bump, refreshDetail]);

  // A "Next steps" quick action drafted a text: hold it for the Composer and
  // REVEAL the Communicator, or the recruiter would never see the draft. On
  // desktop that's the left column's pane (the right column keeps showing Next
  // steps); below lg only one pane is on screen, so switch the whole tab.
  const prefillComposer = useCallback((text: string) => {
    setComposerDraft({ text, mode: 'sms' });
    setLeftTab('comms');
    if (!window.matchMedia('(min-width: 1024px)').matches) setTab('comms');
  }, []);

  // Mobile top-tab pick: show the pane and keep the desktop LEFT-column
  // selection in sync when the pane is a left one.
  const selectMobile = useCallback((key: Tab) => {
    setTab(key);
    if (key === 'script' || key === 'summary' || key === 'comms') setLeftTab(key);
  }, []);

  // ── Resizable Notes pane (desktop only) ──────────────────────────────────
  // The left column's bottom window (Notes) has a draggable height; the top
  // window (Script/Summary/Communicator) flexes to fill the rest. A ref mirrors
  // the height so the pointer/keyboard handlers read the latest without
  // re-subscribing, and the live value is persisted on release (not per frame).
  const leftSectionRef = useRef<HTMLElement | null>(null);
  const [notesHeight, setNotesHeightState] = useState<number>(() => {
    const saved = parseInt(getPref(NOTES_H_PREF, ''), 10);
    return Number.isFinite(saved) ? Math.max(MIN_NOTES_H, saved) : DEFAULT_NOTES_H;
  });
  // Largest the Notes pane may grow to — leaves room for the tab bar + a usable
  // top window. Kept in state so a too-tall saved value re-clamps on a shorter
  // viewport and so the drag handle can expose an accurate aria-valuemax.
  const [notesMaxH, setNotesMaxH] = useState<number>(() => Math.max(DEFAULT_NOTES_H, notesHeight));
  const notesHeightRef = useRef(notesHeight);
  const setNotesHeight = useCallback((h: number) => {
    notesHeightRef.current = h;
    setNotesHeightState(h);
  }, []);
  // Measured from the live left column. Returns null when the section isn't laid
  // out (display:none on mobile) so callers DON'T clamp against a 0 height (which
  // would collapse the persisted value to the minimum).
  const measureMaxNotes = useCallback((): number | null => {
    const sectionH = leftSectionRef.current?.clientHeight ?? 0;
    if (sectionH <= 0) return null;
    return Math.max(MIN_NOTES_H, sectionH - 200);
  }, []);
  const clampNotes = useCallback((h: number) => {
    const maxH = measureMaxNotes() ?? notesMaxH;
    return Math.min(maxH, Math.max(MIN_NOTES_H, h));
  }, [measureMaxNotes, notesMaxH]);

  // Re-clamp against the live section height on mount and on every window resize:
  // a height saved on a tall monitor must not crush the top pane when re-opened
  // on a shorter viewport (Notes is lg:flex-none, non-shrinkable). useLayoutEffect
  // so the correction lands before paint (no flash). Skips while the section is
  // hidden (mobile) so the saved desktop height is preserved for next time.
  useLayoutEffect(() => {
    const sync = () => {
      const maxH = measureMaxNotes();
      if (maxH == null) return;
      setNotesMaxH(maxH);
      setNotesHeight(Math.min(maxH, Math.max(MIN_NOTES_H, notesHeightRef.current)));
    };
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [measureMaxNotes, setNotesHeight]);

  // The active drag's teardown, held in a ref so an unmount mid-drag can also run
  // it (the listeners are on window, not the handle, so they'd otherwise leak).
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const onNotesResizeStart = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.preventDefault();
    const startY = e.clientY;
    const startH = notesHeightRef.current;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'row-resize';
    const onMove = (ev: PointerEvent) => {
      // Drag UP (clientY decreases) grows Notes; drag DOWN shrinks it.
      setNotesHeight(clampNotes(startH + (startY - ev.clientY)));
    };
    const end = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      dragCleanupRef.current = null;
      setPref(NOTES_H_PREF, String(notesHeightRef.current));
    };
    dragCleanupRef.current = end;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end); // touch interrupted by an OS gesture/scroll
  }, [clampNotes, setNotesHeight]);

  const onNotesResizeKey = useCallback((e: React.KeyboardEvent) => {
    const step = (e.shiftKey ? 2 : 1) * NOTES_RESIZE_STEP;
    let next: number;
    if (e.key === 'ArrowUp') next = notesHeightRef.current + step;       // bigger Notes
    else if (e.key === 'ArrowDown') next = notesHeightRef.current - step; // smaller Notes
    else return;
    e.preventDefault();
    next = clampNotes(next);
    setNotesHeight(next);
    setPref(NOTES_H_PREF, String(next));
  }, [clampNotes, setNotesHeight]);

  // Tear down a drag in progress (and restore body styles) if the panel unmounts.
  useEffect(() => () => {
    dragCleanupRef.current?.();
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, []);

  // Future-transcript seam: the prescreen pane registers its setter here so a
  // later autofill panel writes through the same optimistic save path.
  const prescreenApiRef = useRef<PrescreenPaneApi | null>(null);
  const registerPrescreenApi = useCallback((apiObj: PrescreenPaneApi | null) => {
    prescreenApiRef.current = apiObj;
  }, []);
  const handleSuggestAnswer = useCallback((questionKey: string, value: unknown) => {
    prescreenApiRef.current?.setAnswerByKey(questionKey, value);
  }, []);

  // Contact detail (header + lead-linked studies + disposition data).
  useEffect(() => {
    let alive = true;
    api.get(`/api/crm/contacts/${contactId}`)
      .then((res) => { if (alive) setDetail(res.data); })
      .catch(() => { /* header falls back to softphone-known name/number */ });
    return () => { alive = false; };
  }, [contactId]);

  // CRM studies (slug→id resolution + the selector).
  useEffect(() => {
    let alive = true;
    api.get('/api/crm/studies')
      .then((res) => { if (alive) setStudies((res.data?.studies || []).filter((s: StudyOption) => s.id && s.source === 'crm')); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const contactStudySlugs = useMemo(
    () => [...new Set((detail?.leads || []).map((l) => l.study_slug).filter(Boolean))] as string[],
    [detail]
  );

  // Contact's lead-linked studies first, then by lead volume.
  const orderedStudies = useMemo(() => {
    const linked = new Set(contactStudySlugs);
    return [...studies].sort((a, b) => {
      const al = linked.has(a.slug) ? 0 : 1;
      const bl = linked.has(b.slug) ? 0 : 1;
      if (al !== bl) return al - bl;
      return (b.count || 0) - (a.count || 0);
    });
  }, [studies, contactStudySlugs]);

  // The in-call study ALWAYS opens to the one this lead is being CONSIDERED for —
  // the effective primary the detail endpoint resolves (an explicit pin, else the
  // study they came in with). We hold off seeding until the contact detail has
  // loaded so the lighter /studies response can't win the load race and leave an
  // arbitrary (highest-volume) study selected. When there's no considering study
  // we open BLANK and let the recruiter pick one — or just take notes — rather
  // than guessing a default.
  const effectivePrimaryId = detail?.primary_study?.id || detail?.contact?.primary_study_id || null;
  // Once the recruiter picks a study from the header select their choice sticks
  // for the rest of the call; we stop re-seeding from the considering study.
  const studyPinnedByUserRef = useRef(false);
  // Starting a NEW call re-points this same panel onto a new contact (it does NOT
  // remount). Synchronously (pre-paint) drop the prior call's pick + study/detail
  // so the previous lead's study, script or prescreen can't flash for a frame
  // before we re-seed from the new lead's considering study below.
  useLayoutEffect(() => {
    studyPinnedByUserRef.current = false;
    setSelectedStudyId('');
    setStudyDetail(null);
  }, [contactId]);
  useEffect(() => {
    // Seed only once BOTH the studies list and THIS contact's detail (not a stale
    // prior load) are in hand, so we land straight on the considering study with
    // no blank→study flicker.
    if (orderedStudies.length === 0) return;
    if (!detail || detail.contact?.id !== contactId) return;
    if (studyPinnedByUserRef.current) return; // a manual in-call pick wins
    setSelectedStudyId(
      effectivePrimaryId && orderedStudies.some((s) => s.id === effectivePrimaryId)
        ? effectivePrimaryId
        : '',
    );
  }, [detail, contactId, effectivePrimaryId, orderedStudies]);

  const selectedStudy = orderedStudies.find((s) => s.id === selectedStudyId) || null;

  // Changing the in-call study also pins it as the contact's primary ("Considering")
  // study (best-effort — the in-call selection still works if the save fails).
  // Picking the blank placeholder just clears the in-call view; it does NOT clear
  // the lead's considering study.
  const changeStudy = useCallback((studyId: string) => {
    studyPinnedByUserRef.current = true;
    setSelectedStudyId(studyId);
    if (studyId) api.patch(`/api/crm/contacts/${contactId}`, { primary_study_id: studyId }).catch(() => {});
  }, [contactId]);

  // Study detail (artifacts for the script pane, documents for the docs panel).
  useEffect(() => {
    if (!selectedStudyId) { setStudyDetail(null); return; }
    let alive = true;
    setStudyDetailLoading(true);
    api.get(`/api/crm/studies/${selectedStudyId}`)
      .then((res) => { if (alive) setStudyDetail(res.data); })
      .catch(() => { if (alive) setStudyDetail(null); })
      .finally(() => { if (alive) setStudyDetailLoading(false); });
    return () => { alive = false; };
  }, [selectedStudyId]);

  // Registry documents for a linked study (plan §5.1): the docs panel shows
  // eReg's CURRENT documents (legacy uploads remain the fallback for unlinked
  // studies / a degraded registry read). Mapped into the legacy row shape so
  // StudyDocsPanel + the openDoc fallback work unchanged.
  const [registryDocs, setRegistryDocs] = useState<StudyDocument[] | null>(null);
  useEffect(() => {
    const pid = studyDetail?.study?.protocol_id;
    if (!pid || studyDetail?.registry_reads_enabled === false) { setRegistryDocs(null); return; }
    let alive = true;
    api.get(`/api/crm/registry/studies/${pid}/documents`)
      .then((res) => {
        if (!alive) return;
        const TYPE_MAP: Record<string, string> = {
          Protocol: 'protocol',
          'Informed Consent Form': 'icf',
          'Protocol Amendment': 'amendment',
        };
        const docs: StudyDocument[] = (res.data?.documents || [])
          .filter((d: any) => d.status !== 'archived')
          .map((d: any) => ({
            id: d.id,
            doc_type: TYPE_MAP[d.document_type] || 'other',
            filename: d.document_name,
            mime_type: d.mime_type ?? null,
            file_size: null,
            page_count: d.current_version?.page_count ?? null,
            version_label: d.version_number ? `v${d.version_number}` : null,
            extraction_status: d.current_version?.extraction_status || 'complete',
            extraction_error: null,
            created_at: d.updated_at || d.created_at,
            source: 'registry' as const,
          }));
        setRegistryDocs(docs);
      })
      .catch(() => { if (alive) setRegistryDocs(null); });
    return () => { alive = false; };
  }, [studyDetail]);

  const openDoc = useCallback(async (documentId: string) => {
    setOpeningDocId(documentId);
    try {
      await openStudyDoc(documentId);
    } catch { /* user can retry */ }
    finally { setOpeningDocId(null); }
  }, []);

  const contact = detail?.contact;
  const displayName = contact?.full_name
    || [contact?.first_name, contact?.last_name].filter(Boolean).join(' ')
    || active?.name || 'Contact';
  const displayPhone = formatPhone(contact?.phone_e164 || active?.to || null);
  // The lead's real name for the Communicator feed (labels the patient side of
  // call transcripts); undefined when unknown so CommsThread falls back to the
  // number / 'Patient' rather than a generic 'Contact'.
  const threadName = contact?.full_name
    || [contact?.first_name, contact?.last_name].filter(Boolean).join(' ')
    || undefined;

  // The list's outcome control. Logging closes Call Mode once the notes are
  // safe (the call itself is untouched — close() never hangs up); with "Log &
  // next" ticked during a list session the Next-up bar also moves on to the
  // next person.
  const outcomeBlock = myListMember ? (
    <div className="flex flex-wrap items-center gap-2">
      <OutcomeControl
        listId={myListMember.list_id}
        memberId={myListMember.id}
        contactId={contactId}
        surface="callmode"
        goToNext={queue.isListSession && logAndNext}
        onLogged={closeAfterOutcome}
      />
      {queue.isListSession && (
        <label
          className="inline-flex min-h-[44px] cursor-pointer select-none items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 sm:min-h-0"
          title="After logging, go straight to the next person on the list"
        >
          <input
            type="checkbox"
            checked={logAndNext}
            onChange={(e) => setLogAndNext(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
          />
          Log &amp; next
        </label>
      )}
    </div>
  ) : null;

  const study = selectedStudy ? { id: selectedStudy.id!, title: studyInternalLabel(selectedStudy) } : null;
  // On mobile the Script/Notes tabs use the left column; the right column then
  // falls back to the prescreen so it's never blank.
  const rightView: RightTab = LEFT_TABS.has(tab) ? 'prescreen' : (tab as RightTab);

  // Prescreen is rendered PERSISTENTLY below (CSS-hidden when another right tab
  // is active) so its attempt session + in-progress answers survive tab
  // switches; renderRight only handles the on-demand reference panes.
  const renderRight = (view: RightTab) => {
    switch (view) {
      case 'wizard':
        return study
          ? <AiWizardPanel studyId={study.id} onOpenDoc={openDoc} />
          : <p className="p-4 text-sm text-gray-500">Select a study to ask the wizard.</p>;
      case 'docs':
        return (
          <div className="h-full overflow-y-auto">
            <StudyDocsPanel
              documents={registryDocs?.length ? registryDocs : (studyDetail?.documents || [])}
              loading={studyDetailLoading}
              openingId={openingDocId}
              onOpen={openDoc}
            />
          </div>
        );
      case 'next':
        return contact ? (
          <div className="h-full overflow-y-auto">
            {/* Phones: the outreach outcome leads Next steps (the header has
                no room for it below lg). Desktop carries it in the header. */}
            {outcomeBlock && (
              <div className="mb-3 rounded-lg border border-primary-200 bg-primary-50/60 p-2.5 dark:border-primary-800 dark:bg-primary-900/20 lg:hidden">
                <p className="mb-1.5 truncate text-xs font-medium text-primary-800 dark:text-primary-200">
                  On outreach list: {myListMember?.list_name}
                </p>
                {outcomeBlock}
              </div>
            )}
            {/* The in-call study (a manual pick wins) drives the form picker's
                default; before one is seeded, the considering study does. */}
            <CallDispositionBar
              contact={contact}
              studyId={selectedStudy?.id || effectivePrimaryId}
              maxAttempts={detail?.max_attempts}
              reloadToken={reloadToken}
              onSent={bump}
              prefillComposer={prefillComposer}
            />
          </div>
        ) : null;
      default:
        return null;
    }
  };

  const tabBtnCls = (activeTab: boolean) =>
    `inline-flex min-h-[44px] items-center whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 ${
      activeTab
        ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100'
        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
    }`;

  const prescreenPane = study ? (
    useRunner ? (
      <PrescreenRunner
        key={`runner-${study.id}`}
        contactId={contactId}
        study={study}
        onClose={() => setUseRunner(false)}
        onFinished={bump}
      />
    ) : (
      <PrescreenAllPane
        key={`all-${study.id}`}
        contactId={contactId}
        study={study}
        onFinished={bump}
        onApiReady={registerPrescreenApi}
      />
    )
  ) : (
    <p className="p-4 text-sm text-gray-500 dark:text-gray-400">Select a study to start prescreening.</p>
  );

  const prescreenContent = (
    <div className="flex h-full min-h-0 flex-col">
      {study && (
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            Prescreen · {study.title}
          </h3>
          <button
            type="button"
            onClick={() => setUseRunner((v) => !v)}
            title={useRunner ? 'Show every question in one list' : 'Step through one question at a time'}
            className="shrink-0 rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {useRunner ? 'Show all' : 'One-by-one'}
          </button>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">{prescreenPane}</div>
    </div>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Call workspace"
      className={`fixed inset-0 ${Z.overlay} flex flex-col bg-gray-50 dark:bg-gray-900`}
    >
      {/* Header */}
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-gray-200 bg-white px-4 py-2.5 dark:border-gray-700 dark:bg-gray-800">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">{displayName}</span>
            {callEnded ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                <PhoneOff className="h-3 w-3" /> Call ended{endedDuration ? ` · ${endedDuration}` : ''}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
                <PhoneCall className="h-3 w-3" /> {elapsed}
              </span>
            )}
          </div>
          <div className="flex min-w-0 items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <p className="min-w-0 truncate">
              {[
                displayPhone,
                contact?.email,
                contact?.date_of_birth
                  ? `DOB ${formatDob(contact.date_of_birth)}${ageFromDob(contact.date_of_birth) != null ? ` (${ageFromDob(contact.date_of_birth)})` : ''}`
                  : null,
              ].filter(Boolean).join(' · ')}
            </p>
            {contact && (
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                aria-label="Edit contact details"
                title="Edit name, phone, email or date of birth"
                className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-lg px-1.5 py-0.5 font-medium text-primary-600 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-300 dark:hover:bg-primary-900/30 sm:min-h-0"
              >
                <Pencil className="h-3 w-3" />
                {contact.email && contact.date_of_birth ? 'Edit' : 'Add email / DOB'}
              </button>
            )}
          </div>
        </div>

        {/* WCR enrollment line — the number the patient should call/text. */}
        <a
          href={`tel:${WCR_ENROLLMENT_PHONE}`}
          title="WCR enrollment line — the number to give the patient"
          className="inline-flex items-center gap-1.5 rounded-lg border border-primary-200 bg-primary-50 px-2.5 py-1.5 text-sm font-medium text-primary-800 transition-colors duration-150 hover:bg-primary-100 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-200 dark:hover:bg-primary-900/50"
        >
          <Phone className="h-3.5 w-3.5 shrink-0" />
          <span className="hidden sm:inline text-xs font-normal text-primary-600 dark:text-primary-300">Enrollment&nbsp;line</span>
          <span className="tabular-nums">{WCR_ENROLLMENT_PHONE}</span>
        </a>

        {orderedStudies.length > 0 && (
          <select
            value={selectedStudyId}
            onChange={(e) => changeStudy(e.target.value)}
            aria-label="Study being considered"
            title="Study being considered — changing it re-pins this patient's primary study"
            className={`w-full rounded-lg border bg-white px-2.5 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-800 sm:w-auto sm:max-w-[14rem] ${
              selectedStudyId
                ? 'border-gray-300 text-gray-900 dark:border-gray-600 dark:text-gray-100'
                : 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400'
            }`}
          >
            <option value="">— Select a study —</option>
            {orderedStudies.map((s) => (
              <option key={s.id} value={s.id!}>{studyInternalLabel(s)}</option>
            ))}
          </select>
        )}

        {contact && (
          <LifecycleSelect
            contactId={contact.id}
            value={contact.lifecycle_status}
            primaryStudyId={selectedStudyId || effectivePrimaryId}
            onChanged={bump}
          />
        )}

        {/* Desktop: the outreach outcome beside the stage — logged the moment
            the recruiter knows how the call went. */}
        {outcomeBlock && (
          <div className="hidden lg:block" title={`On outreach list: ${myListMember?.list_name ?? ''}`}>
            {outcomeBlock}
          </div>
        )}

        <button
          type="button"
          onClick={onMinimize}
          aria-label="Minimize call workspace (call continues)"
          title="Minimize — the call continues"
          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 sm:min-h-0 sm:min-w-0"
        >
          <Minus className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={onClose}
          aria-label={callEnded ? 'Close call workspace' : 'Close call workspace (call continues in the call bar)'}
          title={callEnded ? 'Close' : 'Close — the call keeps going in the call bar below'}
          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 sm:min-h-0 sm:min-w-0"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      {/* An outcome was logged, but the Notes draft it tried to save first
          did not save — so Call Mode stayed open rather than lose it. On a
          phone the pad is behind its own tab, hence the way to it. */}
      {notesLeftUnsaved && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
        >
          <span className="min-w-0 flex-1">
            The outcome was logged, but your notes didn&apos;t save, so Call Mode stayed open. They are still in Notes — save them, then close.
          </span>
          <button
            type="button"
            onClick={() => selectMobile('notes')}
            className="inline-flex min-h-[44px] items-center rounded-lg border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 transition-colors duration-150 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-red-800 dark:bg-gray-800 dark:text-red-300 dark:hover:bg-red-900/30 sm:min-h-0 lg:hidden"
          >
            Show notes
          </button>
        </div>
      )}

      {/* Mobile tab switcher — every pane is a tab on a phone */}
      <div role="tablist" aria-label="Call workspace panes" className="flex gap-1 overflow-x-auto border-b border-gray-200 bg-gray-100 p-1.5 dark:border-gray-700 dark:bg-gray-900/60 lg:hidden">
        {MOBILE_TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => selectMobile(t.key)} className={tabBtnCls(tab === t.key)}>{t.label}</button>
        ))}
      </div>

      {/* Body */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 p-3 lg:flex-row lg:gap-4">
        {/* LEFT — tabbed Script / Summary / Communicator (always visible on
            desktop, switched with the desktop tab bar) with the Notes
            scratchpad pinned beneath. On mobile each is a top tab. */}
        <section ref={leftSectionRef} className={`${LEFT_TABS.has(tab) ? 'flex' : 'hidden'} min-h-0 flex-1 flex-col gap-3 lg:flex lg:w-1/2`}>
          {/* Desktop left tab bar (mobile uses the top tab switcher). */}
          <div role="tablist" aria-label="Script, summary and communicator" className="hidden gap-1 self-start rounded-lg bg-gray-100 p-1 dark:bg-gray-900/60 lg:flex">
            {LEFT_PANE_TABS.map((t) => (
              <button key={t.key} type="button" role="tab" aria-selected={leftTab === t.key} onClick={() => setLeftTab(t.key)} className={tabBtnCls(leftTab === t.key)}>{t.label}</button>
            ))}
          </div>

          {/* Script — the published recruiter script (the bulk; scrolls). The
              top window flexes to fill the space above the resizable Notes pane.
              With no study selected, prompt the recruiter to pick one (they can
              still take notes below). */}
          <div className={`${tab === 'script' ? 'flex' : 'hidden'} ${leftTab === 'script' ? 'lg:flex' : 'lg:hidden'} min-h-0 flex-1 flex-col`}>
            {study
              ? <CallScriptPane artifacts={studyDetail?.artifacts || []} loading={studyDetailLoading} which="script" />
              : <p className="rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">Select a study to see its script — or just take notes below.</p>}
          </div>

          {/* Summary — the published study summary. */}
          <div className={`${tab === 'summary' ? 'flex' : 'hidden'} ${leftTab === 'summary' ? 'lg:flex' : 'lg:hidden'} min-h-0 flex-1 flex-col`}>
            {study
              ? <CallScriptPane artifacts={studyDetail?.artifacts || []} loading={studyDetailLoading} which="summary" />
              : <p className="rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">Select a study to see its summary — or just take notes below.</p>}
          </div>

          {/* Communicator — this lead's unified comms feed + composer, so the
              recruiter can read history and fire off a text/email mid-call.
              Mounted persistently (Realtime stays live, a half-typed draft
              survives tab switches). While a call is LIVE the bottom-right
              SoftphoneBar (w-72, z-50) floats over this z-40 workspace and would
              cover the composer's Send button on a phone — pb lifts it clear
              (only while the bar is up; desktop's left half never overlaps it). */}
          <div className={`${tab === 'comms' ? 'flex' : 'hidden'} ${leftTab === 'comms' ? 'lg:flex' : 'lg:hidden'} min-h-0 flex-1 flex-col ${callEnded ? '' : 'pb-44'} lg:pb-0`}>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
              <CommsThread
                contactId={contactId}
                reloadToken={commsToken}
                usersById={usersById}
                onPing={refreshDetail}
                contactName={threadName}
                contactPhone={contact?.phone_e164 ?? null}
              />
              <Composer
                key={contactId}
                contactId={contactId}
                studyId={study?.id ?? null}
                contactName={threadName}
                smsOptOut={contact?.sms_opt_out ?? false}
                hasPhone={!!contact?.phone_e164}
                hasEmail={!!contact?.email}
                onSent={onCommsSent}
                prefill={composerDraft}
                onPrefillConsumed={() => setComposerDraft(null)}
              />
            </div>
          </div>

          {/* Drag handle (desktop only) — resize the Notes window up/down. */}
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize the Notes panel"
            aria-valuenow={Math.round(notesHeight)}
            aria-valuemin={MIN_NOTES_H}
            aria-valuemax={Math.round(notesMaxH)}
            tabIndex={0}
            onPointerDown={onNotesResizeStart}
            onKeyDown={onNotesResizeKey}
            title="Drag to resize Notes (or focus and use ↑/↓)"
            className="group hidden shrink-0 cursor-row-resize touch-none select-none items-center justify-center py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 lg:flex"
          >
            <div className="h-1.5 w-10 rounded-full bg-gray-300 transition-colors duration-150 group-hover:bg-primary-400 group-focus-visible:bg-primary-500 dark:bg-gray-600 dark:group-hover:bg-primary-500" />
          </div>

          {/* Notes — pinned always-visible on desktop at a draggable height
              (defaults to ~3 lines); its own full tab on mobile (flex-1). */}
          <div
            style={{ '--nh': `${notesHeight}px` } as React.CSSProperties}
            className={`${tab === 'notes' ? 'flex' : 'hidden'} min-h-0 flex-1 flex-col lg:flex lg:h-[var(--nh)] lg:flex-none`}
          >
            <div className="mb-1.5 hidden text-sm font-semibold text-gray-900 dark:text-gray-100 lg:block">Notes</div>
            <div className="min-h-0 flex-1">
              <NotesScratchpad
                contactId={contactId}
                contactName={contact?.id === contactId ? threadName : undefined}
                onSaved={onNotesSaved}
                onApiReady={registerNotesApi}
              />
            </div>
          </div>
        </section>

        {/* RIGHT — Prescreen / AI wizard / Docs / Next steps (click-through) */}
        <section className={`${LEFT_TABS.has(tab) ? 'hidden' : 'flex'} min-h-0 flex-1 flex-col lg:flex lg:w-1/2`}>
          <div role="tablist" aria-label="Reference panes" className="mb-2 hidden gap-1 self-start rounded-lg bg-gray-100 p-1 dark:bg-gray-900/60 lg:flex">
            {RIGHT_TABS.map((t) => (
              <button key={t.key} type="button" role="tab" aria-selected={rightView === t.key} onClick={() => setTab(t.key)} className={tabBtnCls(rightView === t.key)}>{t.label}</button>
            ))}
          </div>
          {/* While a call is LIVE the bottom-right SoftphoneBar (w-72, z-50) floats
              over this z-40 workspace and — UNLIKE the left half — overlaps this
              right column too (it sits on the right). It covers the pinned
              prescreen footer's right-aligned "Mark eligible" CTA (and the
              Next-steps disposition buttons) on BOTH desktop and mobile. Reserve
              bottom clearance only while the bar is up. The padding shrinks this
              wrapper's content box, which cascades down every h-full/flex-1 link
              to lift PrescreenAllPane's pinned footer (a sibling of its scroll
              region). lg:pb-44 is needed explicitly so it isn't wiped by lg:p-0. */}
          <div className={`min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 lg:border-0 lg:bg-transparent lg:p-0 lg:dark:bg-transparent ${callEnded ? '' : 'pb-44 lg:pb-44'}`}>
            {/* Prescreen stays MOUNTED across tab switches (answers + attempt
                session survive); the other panes mount on demand. */}
            <div className={`${rightView === 'prescreen' ? 'block' : 'hidden'} h-full`}>
              {prescreenContent}
            </div>
            {rightView !== 'prescreen' && renderRight(rightView)}
          </div>
        </section>
      </div>

      {editOpen && contact && (
        <EditContactModal
          contact={contact}
          onClose={() => setEditOpen(false)}
          onSaved={(updated: Contact) => {
            setEditOpen(false);
            if (updated) {
              setDetail((d) => (d ? { ...d, contact: { ...d.contact, ...updated } } : d));
            }
          }}
        />
      )}

      {/* Reserved seam for the future live-transcript / AI-autofill panel. */}
      <TranscriptSlot
        contactId={contactId}
        callSid={lastCall?.callSid ?? null}
        studyId={study?.id ?? null}
        startedAt={startedAt}
        onSuggestAnswer={handleSuggestAnswer}
      />
    </div>
  );
}
